import { Trans } from '@lingui/react/macro';

import type { ImageAnalyses } from '../../../../@types/latestImagesV2';
import type { TFlattenedTreatment } from '../../../../@types/rcm';

const sortTeeth = (teeth: string[]) => {
  return teeth.sort((a, b) => {
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    return aNum - bNum;
  });
};

const getAllTeeth = (images: ImageAnalyses[]) => {
  const teeth = images.flatMap((image) =>
    image.teeth.map((tooth) => tooth.toothId),
  );
  return sortTeeth(Array.from(new Set(teeth)));
};

const getAllTreatmentTeeth = (treatments: TFlattenedTreatment[]) => {
  const teeth = Array.from(
    new Set(
      treatments.flatMap(
        // only adult teeth
        (t) => t.teeth?.filter((tooth) => !isNaN(parseInt(tooth))) ?? [],
      ),
    ),
  );
  return sortTeeth(teeth);
};

const Header = ({ title }: { title: string }) => (
  <h3 className="vai-text-lg vai-font-semibold vai-mb-2">{title}</h3>
);

export default function DevTool({
  images,
  filteredImages,
  treatments,
  filteredTreatments,
}: {
  images: ImageAnalyses[];
  filteredImages: ImageAnalyses[];
  treatments: TFlattenedTreatment[];
  filteredTreatments: TFlattenedTreatment[];
}) {
  const allTeeth = getAllTeeth(images);
  const allTeethFiltered = getAllTeeth(filteredImages);

  const allTreatmentTeeth = getAllTreatmentTeeth(treatments);
  const allTreatmentTeethFiltered = getAllTreatmentTeeth(filteredTreatments);

  const TeethDisplay = ({ teeth }: { teeth: string[] }) => (
    <div className="vai-flex vai-flex-wrap vai-gap-2">
      {teeth.map((t) => (
        <div
          key={t}
          className="vai-bg-gray-100 vai-p-2 vai-rounded vai-min-w-[40px] vai-text-center"
        >
          {t}
        </div>
      ))}
    </div>
  );

  return (
    <div className="vai-overflow-y-auto">
      <div className="vai-p-4">
        <div className="vai-mb-4">
          <Header title="All" />
          <p className="vai-text-sm vai-font-semibold">
            <Trans>Image Teeth</Trans>
          </p>
          <TeethDisplay teeth={allTeeth} />
          <p className="vai-text-sm vai-font-semibold">
            <Trans>Treatment Teeth</Trans>
          </p>
          <TeethDisplay teeth={allTreatmentTeeth} />
        </div>
        <div>
          <Header title="Default" />
          <p className="vai-text-sm vai-font-semibold">
            <Trans>Image Teeth</Trans>
          </p>
          <TeethDisplay teeth={allTeethFiltered} />
          <p className="vai-text-sm vai-font-semibold">
            <Trans>Treatment Teeth</Trans>
          </p>
          <TeethDisplay teeth={allTreatmentTeethFiltered} />
        </div>
      </div>
    </div>
  );
}
